/* ------------------------------------------------------------------
 * Magni Scroll Sequence — horizontal card swiper (all breakpoints)
 *
 * Each frame is a scroll-snap card in a horizontal track; a dots pill +
 * play/pause (built here) track / drive position. Desktop shows wider
 * cards with neighbours peeking and adds click-drag-to-scroll for mouse
 * users (touch swipes natively). Layout differences are pure CSS.
 * ------------------------------------------------------------------ */

(function () {
  'use strict';

  var AUTOPLAY_MS = 4500;
  var DRAG_THRESHOLD = 4; // px of movement before a press counts as a drag

  var PLAY_ICON = '<svg viewBox="0 0 24 24" aria-hidden="true"><path d="M8 5v14l11-7z"/></svg>';
  var PAUSE_ICON = '<svg viewBox="0 0 24 24" aria-hidden="true"><path d="M6 5h4v14H6zM14 5h4v14h-4z"/></svg>';
  var PREV_ICON = '<svg viewBox="0 0 24 24" aria-hidden="true"><path d="M14.5 5.5 8 12l6.5 6.5"/></svg>';
  var NEXT_ICON = '<svg viewBox="0 0 24 24" aria-hidden="true"><path d="M9.5 5.5 16 12l-6.5 6.5"/></svg>';

  // Remembers the active card per section id so a Shopify customizer re-render
  // (which fires shopify:section:load and rebuilds the DOM) doesn't snap back
  // to the first slide while a later card's sliders are being tuned.
  // Lives on window (not a plain var) because the theme editor re-executes this
  // in-section <script> on every reload — a plain var would reset to {} and lose
  // the active card. See the bind guard at the bottom.
  var activeIndexBySection = window.MagniScrollState || (window.MagniScrollState = {});

  var REDUCE = window.matchMedia('(prefers-reduced-motion: reduce)').matches;

  function initCarousel(section) {
    var track = section.querySelector('.magni-scroll__images');
    var frames = section.querySelectorAll('.magni-scroll__frame');
    var count = frames.length;
    var sid = section.id;
    if (!track || count < 2) return;
    if (section.querySelector('.magni-scroll__nav')) return; // already built

    // Enable the per-card caption reveal (text fades in from the side when a card
    // becomes active). JS-gated so no-JS / pre-init still shows the text.
    section.classList.add('magni-scroll--textanim');

    // Clear any stale inline opacity/transform (CSS reveals the cards now).
    for (var f = 0; f < frames.length; f++) {
      frames[f].style.opacity = '';
      frames[f].style.transform = '';
      frames[f].style.visibility = '';
    }

    var nav = document.createElement('div');
    nav.className = 'magni-scroll__nav';

    var dotsWrap = document.createElement('div');
    dotsWrap.className = 'magni-scroll__dots';
    var dots = [];
    for (var i = 0; i < count; i++) {
      var dot = document.createElement('button');
      dot.type = 'button';
      dot.className = 'magni-scroll__dot' + (i === 0 ? ' is-active' : '');
      dot.setAttribute('aria-label', 'Go to slide ' + (i + 1));
      (function (idx) { dot.addEventListener('click', function () { stop(); scrollToCard(idx); }); })(i);
      dotsWrap.appendChild(dot);
      dots.push(dot);
    }

    var playBtn = document.createElement('button');
    playBtn.type = 'button';
    playBtn.className = 'magni-scroll__playpause';
    playBtn.setAttribute('aria-label', 'Play slideshow');
    playBtn.innerHTML = PLAY_ICON;

    // Prev/next arrows, pinned to the right of the nav row (desktop-only via CSS).
    var arrows = document.createElement('div');
    arrows.className = 'magni-scroll__arrows';
    var prevBtn = document.createElement('button');
    prevBtn.type = 'button';
    prevBtn.className = 'magni-scroll__arrow';
    prevBtn.setAttribute('aria-label', 'Previous slide');
    prevBtn.innerHTML = PREV_ICON;
    var nextBtn = document.createElement('button');
    nextBtn.type = 'button';
    nextBtn.className = 'magni-scroll__arrow';
    nextBtn.setAttribute('aria-label', 'Next slide');
    nextBtn.innerHTML = NEXT_ICON;
    prevBtn.addEventListener('click', function () { stop(); scrollToCard(Math.max(0, activeIndex() - 1)); });
    nextBtn.addEventListener('click', function () { stop(); scrollToCard(Math.min(count - 1, activeIndex() + 1)); });
    arrows.appendChild(prevBtn);
    arrows.appendChild(nextBtn);

    nav.appendChild(dotsWrap);
    nav.appendChild(playBtn);
    nav.appendChild(arrows);
    var host = section.querySelector('.magni-scroll__sticky') || section;
    host.appendChild(nav);

    function step() {
      return frames.length > 1 ? (frames[1].offsetLeft - frames[0].offsetLeft) : track.clientWidth;
    }
    function activeIndex() {
      var idx = Math.round(track.scrollLeft / step());
      return Math.max(0, Math.min(count - 1, idx));
    }
    function setActive(idx) {
      for (var j = 0; j < dots.length; j++) dots[j].classList.toggle('is-active', j === idx);
      // Mark the active card too so its caption replays the reveal animation.
      for (var f2 = 0; f2 < frames.length; f2++) frames[f2].classList.toggle('is-active', f2 === idx);
      prevBtn.disabled = idx === 0;
      nextBtn.disabled = idx === count - 1;
    }
    // Center card `idx` in the track (more accurate than idx*step under the
    // scroll-padding, and avoids scrollIntoView yanking the page vertically).
    function centerLeft(idx) {
      var fr = frames[idx];
      if (!fr) return idx * step();
      return Math.max(0, fr.offsetLeft - (track.clientWidth - fr.clientWidth) / 2);
    }
    function scrollToCard(idx, instant) {
      track.scrollTo({ left: centerLeft(idx), behavior: (instant || REDUCE) ? 'auto' : 'smooth' });
    }
    // Expose so the design-mode block-select handler + resize re-center can drive this carousel.
    section._magniScrollToCard = scrollToCard;
    section._magniActiveIndex = activeIndex;

    var ticking = false;
    track.addEventListener('scroll', function () {
      if (!ticking) {
        window.requestAnimationFrame(function () {
          var ai = activeIndex();
          setActive(ai);
          activeIndexBySection[sid] = ai;
          ticking = false;
        });
        ticking = true;
      }
    }, { passive: true });

    // ---- Click-drag to scroll (desktop mouse — touch swipes natively) ----
    // Pointer capture keeps move/up on the track, so customizer re-renders
    // (which rebuild the track) don't stack window-level listeners.
    var down = false, dragged = false, startX = 0, startScroll = 0;
    track.addEventListener('pointerdown', function (e) {
      if (e.pointerType === 'touch') return; // native touch scrolling
      down = true; dragged = false;
      startX = e.clientX; startScroll = track.scrollLeft;
      track.classList.add('is-dragging');
      try { track.setPointerCapture(e.pointerId); } catch (_) {}
    });
    track.addEventListener('pointermove', function (e) {
      if (!down) return;
      var dx = e.clientX - startX;
      if (Math.abs(dx) > DRAG_THRESHOLD) dragged = true;
      if (dragged) {
        e.preventDefault();
        track.scrollLeft = startScroll - dx;
      }
    });
    function endDrag(e) {
      if (!down) return;
      down = false;
      track.classList.remove('is-dragging');
      if (e && e.pointerId != null) { try { track.releasePointerCapture(e.pointerId); } catch (_) {} }
      if (dragged) { stop(); scrollToCard(activeIndex()); } // settle to nearest card
    }
    track.addEventListener('pointerup', endDrag);
    track.addEventListener('pointercancel', endDrag);

    // Click a peeking (non-centered) card to bring it over. Skipped right after
    // a drag (that press already settled to the nearest card). Falls back to
    // hit-testing the pointer when pointer-capture leaves e.target on the track.
    track.addEventListener('click', function (e) {
      if (dragged) return;
      var card = e.target.closest && e.target.closest('.magni-scroll__frame');
      if (!card) {
        var el = document.elementFromPoint(e.clientX, e.clientY);
        card = el && el.closest && el.closest('.magni-scroll__frame');
      }
      if (!card) return;
      var idx = Array.prototype.indexOf.call(frames, card);
      if (idx < 0 || idx === activeIndex()) return; // clicking the active card does nothing
      stop();
      scrollToCard(idx);
    });

    // Autoplay (opt-in via the play button); advances only while in view.
    var playing = false, timer = null;
    function advance() { scrollToCard((activeIndex() + 1) % count); }
    function start() {
      if (playing) return;
      playing = true;
      playBtn.innerHTML = PAUSE_ICON;
      playBtn.setAttribute('aria-label', 'Pause slideshow');
      timer = window.setInterval(advance, AUTOPLAY_MS);
    }
    function stop() {
      if (!playing) return;
      playing = false;
      playBtn.innerHTML = PLAY_ICON;
      playBtn.setAttribute('aria-label', 'Play slideshow');
      if (timer) { window.clearInterval(timer); timer = null; }
    }
    playBtn.addEventListener('click', function () { playing ? stop() : start(); });
    track.addEventListener('touchstart', function () { stop(); dragged = false; }, { passive: true });

    if ('IntersectionObserver' in window) {
      var io = new IntersectionObserver(function (entries) {
        for (var k = 0; k < entries.length; k++) {
          if (!entries[k].isIntersecting) stop();
        }
      }, { threshold: 0.35 });
      io.observe(section);
    }

    // Restore the card we were on before a customizer re-render (skip 0 —
    // that's already the default position). Wait a frame for layout to settle.
    var restoreIdx = activeIndexBySection[sid] || 0;
    window.requestAnimationFrame(function () {
      if (restoreIdx) scrollToCard(restoreIdx, true);
      setActive(restoreIdx); // marks the starting card so its caption reveals
    });
  }

  function initSection(section) {
    initCarousel(section);
  }

  function init() {
    var sections = document.querySelectorAll('[data-magni-scroll]');
    for (var i = 0; i < sections.length; i++) initSection(sections[i]);
  }

  // Bind the one-time init + global listeners only once. The theme editor
  // re-runs this in-section <script> on each section reload; bail here so we
  // don't stack duplicate scroll/resize/section:load listeners. The persistent
  // section:load listener (first run) re-inits the rebuilt DOM.
  if (window.MagniScrollBound) return;
  window.MagniScrollBound = true;

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', init);
  } else {
    init();
  }

  // Keep the active card centered after a viewport resize (card widths are vw-
  // based, so the centered scroll offset shifts). Lives here (bind-once) so it
  // doesn't stack across customizer re-renders.
  window.addEventListener('resize', function () {
    var secs = document.querySelectorAll('[data-magni-scroll]');
    for (var i = 0; i < secs.length; i++) {
      var s = secs[i];
      if (typeof s._magniScrollToCard === 'function') {
        s._magniScrollToCard(activeIndexBySection[s.id] || 0, true);
      }
    }
  }, { passive: true });

  document.addEventListener('shopify:section:load', function (e) {
    var section = e.target.querySelector('[data-magni-scroll]');
    if (section) initSection(section);
  });

  // Design mode only: when a frame block is selected/edited in the theme
  // editor, jump the carousel to that card instead of letting the re-render
  // snap back to the first slide.
  if (window.Shopify && window.Shopify.designMode) {
    document.addEventListener('shopify:block:select', function (e) {
      var section = e.target.closest && e.target.closest('[data-magni-scroll]');
      if (!section || typeof section._magniScrollToCard !== 'function') return;
      var frames = section.querySelectorAll('.magni-scroll__frame');
      var frame = e.target.closest('.magni-scroll__frame');
      var idx = frame ? Array.prototype.indexOf.call(frames, frame) : -1;
      if (idx < 0) return;
      activeIndexBySection[section.id] = idx;
      section._magniScrollToCard(idx);
    });
  }
})();
